import AsyncStorage from "@react-native-async-storage/async-storage";
import jwtDecode from "jwt-decode";
import { emitAuthLogout, emitTokensUpdated } from "./authEvents";

const GRAPHQL_ENDPOINT = typeof window === "undefined" ? "/api/graphql" : `${window.location.origin}/api/graphql`;

const REFRESH_MUTATION =
  "mutation RefreshToken($refreshToken: String!) {\n  refreshToken(refreshToken: $refreshToken) {\n    accessToken\n    refreshToken\n  }\n}";

type DecodedToken = {
  exp?: number;
};

type RefreshResult = {
  accessToken: string;
  refreshToken: string;
};

let refreshInFlight: Promise<RefreshResult | null> | null = null;
let logoutEmitted = false;
let logoutInProgress = false;

export const setLogoutInProgress = (value: boolean) => {
  logoutInProgress = value;
  if (value) {
    refreshInFlight = null;
  }
};

const getTokenExpiryMs = (token: string): number | null => {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isTokenExpired = (token: string, bufferSeconds = 0): boolean => {
  const expiryMs = getTokenExpiryMs(token);
  if (!expiryMs) {
    return true;
  }
  return expiryMs <= Date.now() + bufferSeconds * 1000;
};

export const storeAuthTokens = async (accessToken: string, refreshToken: string) => {
  if (logoutInProgress) {
    return;
  }
  await AsyncStorage.setItem("accessToken", accessToken);
  await AsyncStorage.setItem("refreshToken", refreshToken);
  logoutEmitted = false;
  emitTokensUpdated(accessToken, refreshToken);
};

export const resetLogoutGuard = () => {
  logoutEmitted = false;
};

export const storeAccessToken = async (accessToken: string) => {
  await AsyncStorage.setItem("accessToken", accessToken);
  resetLogoutGuard();
};

export const clearStoredTokens = async (reason?: string) => {
  await AsyncStorage.removeItem("accessToken");
  await AsyncStorage.removeItem("refreshToken");
  if (!logoutEmitted) {
    logoutEmitted = true;
    emitAuthLogout(reason);
  }
};

const runRefresh = async (): Promise<RefreshResult | null> => {
  if (logoutInProgress) {
    return null;
  }
  const refreshToken = await AsyncStorage.getItem("refreshToken");
  if (!refreshToken) {
    await clearStoredTokens("missing_refresh_token");
    return null;
  }

  try {
    const res = await fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        operationName: "RefreshToken",
        query: REFRESH_MUTATION,
        variables: { refreshToken },
      }),
    });

    if (!res.ok) {
      await clearStoredTokens(`refresh_failed_${res.status}`);
      return null;
    }

    const json = await res.json();
    if (json?.errors) {
      await clearStoredTokens("refresh_failed_graphql");
      return null;
    }

    const data = json?.data?.refreshToken;
    if (!data?.accessToken || !data?.refreshToken) {
      await clearStoredTokens("refresh_failed_missing_tokens");
      return null;
    }

    if (logoutInProgress) {
      return null;
    }
    await storeAuthTokens(data.accessToken, data.refreshToken);
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  } catch {
    await clearStoredTokens("refresh_failed_exception");
    return null;
  }
};

export const refreshAccessToken = async (): Promise<RefreshResult | null> => {
  if (logoutInProgress) {
    return null;
  }
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = runRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

export const getValidAccessToken = async (options?: {
  forceRefresh?: boolean;
  minTtlSeconds?: number;
}): Promise<string | null> => {
  if (logoutInProgress) {
    return null;
  }
  if (options?.forceRefresh) {
    const refreshed = await refreshAccessToken();
    return refreshed?.accessToken ?? null;
  }

  const accessToken = await AsyncStorage.getItem("accessToken");
  const minTtlSeconds = options?.minTtlSeconds ?? 30;

  if (accessToken && !isTokenExpired(accessToken, minTtlSeconds)) {
    return accessToken;
  }

  const refreshed = await refreshAccessToken();
  return refreshed?.accessToken ?? null;
};

export const getAuthHeader = async (): Promise<{ Authorization?: string }> => {
  const token = await getValidAccessToken();
  if (!token) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
};
